import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { ProfileContext } from "../context/ProfileContext";
import {
  HEADERTEXT_TYPES,
  ASSOCIATESTATUS_TYPES,
} from "../functions/postActionTypes";
import {
  createAssociateSearchFilter,
  createCrewSearchFilter,
  segmentAssociateRequests,
  sortAssociatesByName,
} from "../utils/associateFilters";
import {
  IonContent,
  IonPage,
  IonRow,
  IonCol,
  IonSearchbar,
  IonLabel,
  IonBadge,
  IonIcon,
  IonSegment,
  IonSegmentButton,
  useIonAlert,
  useIonViewWillEnter,
} from "@ionic/react";
import { chevronDown, chevronForward } from "ionicons/icons";
import { WerkerSchedulerHeader } from "../components/WerkHeader";
import TabNavigator from "../components/TabNavigator";
import { AssociatesList } from "../components/AssociatesList";
import { AuthContext } from "../context/AuthContext";
import { Redirect, useHistory } from "react-router-dom";
import { LoadingScreen } from "../components/LoadingScreen";
import { Crew, CrewContext } from "../context/CrewContext";
import {
  CrewsListSection,
  CreateCrewModal,
  EditCrewMembersModal,
  CrewDetailsModal,
} from "../components/crews";
import { useCrewMemberEditor } from "../hooks/useCrewMemberEditor";
import { isDesktop } from "../utils/platformUtils";
import AssociatesDesktop from "./desktop/AssociatesDesktop";

type AssociatesViewMode = "associates" | "crews";

const getInitials = (name: string) => {
  const parts = (name || "").trim().split(" ").filter(Boolean);
  if (parts.length === 0) return "C";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
};

const Associates: React.FC = () => {
  const [searchText, setSearchText] = useState("");
  const [viewMode, setViewMode] = useState<AssociatesViewMode>(
    () => (sessionStorage.getItem("associates_view_mode") as AssociatesViewMode) ?? "associates"
  );
  const [incomingExpanded, setIncomingExpanded] = useState(false);

  // Crews (UI scaffold for now)
  const [selectedCrew, setSelectedCrew] = useState<Crew | null>(null);
  const [crewModalOpen, setCrewModalOpen] = useState(false);
  const [editingCrewId, setEditingCrewId] = useState<string | null>(null);
  const [editMembersOpen, setEditMembersOpen] = useState(false);
  const [deletingCrew, setDeletingCrew] = useState(false);

  // Members selection (single source of truth)
  const [selectedMemberIds, setSelectedMemberIds] = useState<string[]>([]);
  const [savingMembers, setSavingMembers] = useState(false);

  // Edit Members filter (ONLY in modal)
  const [memberSearchText, setMemberSearchText] = useState("");
  const [showSelectedOnly, setShowSelectedOnly] = useState(false);

  const {
    crews,
    crewsLoading,
    createCrew,
    updateCrewMembers,
    updateCrewDetails,
    deleteCrew,
  } = useContext(CrewContext);

  const [createCrewOpen, setCreateCrewOpen] = useState(false);
  const [newCrewName, setNewCrewName] = useState("");
  const [newCrewDescription, setNewCrewDescription] = useState("");
  const [savingCrew, setSavingCrew] = useState(false);
  const [pendingOpenCrewId, setPendingOpenCrewId] = useState<string | null>(
    null
  );

  const [presentAlert] = useIonAlert();

  const blurFocusedElement = useCallback(() => {
    let activeElement: Element | null = document.activeElement;

    while (activeElement && (activeElement as HTMLElement).shadowRoot?.activeElement) {
      activeElement = (activeElement as HTMLElement).shadowRoot?.activeElement || null;
    }

    if (activeElement instanceof HTMLElement && activeElement !== document.body) {
      activeElement.blur();
    }

    const hostElement = document.activeElement;
    if (hostElement instanceof HTMLElement && hostElement !== document.body) {
      hostElement.blur();
    }
  }, []);

  const {
    myAssociates,
    myRequests,
    everyoneElse,
    badgeCountAssociateRequestsContext,
    profile,
    refreshEveryoneElse,
  } = useContext(ProfileContext);

  // Fetch the full public-facing user list when this page becomes active.
  // Replaces the former persistent real-time listener on the entire user table.
  useIonViewWillEnter(() => {
    refreshEveryoneElse();
  });

  // useIonViewWillEnter requires an IonPage ancestor, which the desktop branch
  // below does not render — so a plain mount effect covers desktop too.
  useEffect(() => {
    refreshEveryoneElse();
  }, [refreshEveryoneElse]);

  const openEditMembers = () => {
    blurFocusedElement();
    const existing = Array.isArray(selectedCrew?.memberIds)
      ? (selectedCrew!.memberIds as string[])
      : [];

    setSelectedMemberIds(existing);

    // IMPORTANT: reset modal-only filter each time you open
    setMemberSearchText("");
    setEditMembersOpen(true);
    setShowSelectedOnly(false);
  };

  const closeEditMembers = () => {
    blurFocusedElement();
    setEditMembersOpen(false);
    setMemberSearchText("");
  };

  const canSeeCrews = !!profile?.UserLastRoleScheduler;

  const history = useHistory();

  const toggleMember = (uid: string, checked: boolean) => {
    setSelectedMemberIds((prev) => {
      const set = new Set(prev);
      if (checked) set.add(uid);
      else set.delete(uid);
      return Array.from(set);
    });
  };

  useEffect(() => {
    // Only reset if profile is fully loaded — avoids reacting to transient
    // falsy UserLastRoleScheduler values during mid-update Firestore snapshots
    if (profile.UserId !== "" && !canSeeCrews && viewMode === "crews") {
      sessionStorage.setItem("associates_view_mode", "associates");
      setViewMode("associates");
    }
  }, [canSeeCrews, viewMode, profile.UserId]);

  // Auto-open newly created crew when snapshot updates /////////////////////
  useEffect(() => {
    if (!pendingOpenCrewId) return;

    const found = crews.find((c) => c.id === pendingOpenCrewId);
    if (!found) return;

    setSelectedCrew(found);
    setCrewModalOpen(true);
    setPendingOpenCrewId(null);
  }, [pendingOpenCrewId, crews, crewModalOpen]);

  // Keep selectedCrew up to date with latest crew data /////////////////////
  useEffect(() => {
    if (!crewModalOpen) return;
    if (!selectedCrew?.id) return;

    const latest = crews.find((c) => c.id === selectedCrew.id);
    if (!latest) return;

    const changed =
      latest.memberCount !== selectedCrew.memberCount ||
      (latest.description || "") !== (selectedCrew.description || "") ||
      (latest.name || "") !== (selectedCrew.name || "");

    if (changed) {
      setSelectedCrew(latest);
    }
  }, [crews, crewModalOpen, selectedCrew?.id, selectedCrew?.memberCount, selectedCrew?.description, selectedCrew?.name]);

  const { currentUser, authReady } = useContext(AuthContext);

  const searchTextFunction = (typedValue: string) => {
    setSearchText(typedValue);
  };

  const searchLower = useMemo(
    () => searchText.trim().toLowerCase(),
    [searchText]
  );

  // Use shared filter utility
  const matchesSearch = useMemo(
    () => createAssociateSearchFilter(searchText),
    [searchText]
  );

  // Base buckets using shared segmentation utility
  const { incomingRequests: incomingRequestsBase, sentRequests: sentRequestsBase } = useMemo(() => {
    return segmentAssociateRequests(
      myRequests, 
      ASSOCIATESTATUS_TYPES.RequestReceived,
      ASSOCIATESTATUS_TYPES.RequestSent
    );
  }, [myRequests]);

  const associatesSortedBase = useMemo(() => {
    return sortAssociatesByName(myAssociates);
  }, [myAssociates]);

  // Filtered buckets (page search)
  const incomingRequests = useMemo(() => {
    return incomingRequestsBase.filter(matchesSearch);
  }, [incomingRequestsBase, matchesSearch]);

  const associatesSorted = useMemo(() => {
    return associatesSortedBase.filter(matchesSearch);
  }, [associatesSortedBase, matchesSearch]);

  const everyoneFiltered = useMemo(() => {
    // Merge sent requests into "Everyone" so they appear naturally with their
    // "Request Sent" indicator instead of a separate section
    const merged = [...(everyoneElse || []), ...sentRequestsBase];
    return merged.filter(matchesSearch);
  }, [everyoneElse, sentRequestsBase, matchesSearch]);

  const showEveryone = useMemo(() => searchLower.length > 0, [searchLower]);

  const unreadIncomingCount = badgeCountAssociateRequestsContext || 0;

  // Crews View: Filtered Crews List
  // ============================
  const crewsFiltered = useMemo(() => {
    if (searchText === "") return crews;
    const matchesCrew = createCrewSearchFilter(searchText);
    return (crews || []).filter(matchesCrew);
  }, [crews, searchText]);

  // Crew Details Member List /////////////////////////////////////
  const {
    crewMembersResolved,
    memberSearchLower,
    filteredSortedMembers,
    visibleUids,
    allVisibleSelected,
    someVisibleSelected,
    visibleSelectedCount,
    toggleSelectAllVisible,
  } = useCrewMemberEditor({
    myAssociates,
    selectedCrew,
    memberSearchText,
    showSelectedOnly,
    selectedMemberIds,
    setSelectedMemberIds,
  });

  // Create Crew Handlers
  const openCreateCrew = () => {
    blurFocusedElement();
    // Show paywall for non-subscribers
    if (profile.SubscriptionType !== "Scheduler") {
      history.push({
        pathname: "/Paywall",
        state: { cameFrom: "Home.Scheduler" },
      });
      return;
    }
    setEditingCrewId(null);
    setNewCrewName("");
    setNewCrewDescription("");
    setCreateCrewOpen(true);
  };

  const closeCreateCrew = () => {
    blurFocusedElement();
    setCreateCrewOpen(false);
    setEditingCrewId(null);
  };

  const openEditCrewDetails = () => {
    if (!selectedCrew?.id) return;

    blurFocusedElement();

    setEditingCrewId(selectedCrew.id);
    setNewCrewName(selectedCrew.name || "");
    setNewCrewDescription(selectedCrew.description || "");
    setCreateCrewOpen(true);
  };

  // // Save Crew Details Handler
  const handleSaveCrewDetails = async () => {
    try {
      const nameTrim = (newCrewName || "").trim();
      if (!nameTrim) {
        await presentAlert({
          header: "Crew Name Required",
          message: "Please enter a crew name.",
          buttons: ["OK"],
        });
        return;
      }

      setSavingCrew(true);

      const payload = {
        name: nameTrim,
        description: (newCrewDescription || "").trim(),
      };

      if (!editingCrewId) {
        // CREATE
        const newId = await createCrew(payload);

        setSavingCrew(false);
        setCreateCrewOpen(false);
        setEditingCrewId(null);

        setPendingOpenCrewId(newId);
        // console.log("Created crew id:", newId);
        return;
      }

      // EDIT
      await updateCrewDetails({
        crewId: editingCrewId,
        name: payload.name,
        description: payload.description,
      });

      setSavingCrew(false);
      setCreateCrewOpen(false);
      setEditingCrewId(null);

      // console.log("Updated crew id:", editingCrewId);
    } catch (err: any) {
      console.error("Save crew details error:", err);
      setSavingCrew(false);

      await presentAlert({
        header: editingCrewId ? "Couldn’t Update Crew" : "Couldn’t Create Crew",
        message: err?.message || "Unknown error",
        buttons: ["OK"],
      });
    }
  };
  // //////////////////////////////////////////////
  // Delete Crew Handler
  const handleDeleteCrew = async () => {
    try {
      if (!selectedCrew?.id) return;

      // Don’t allow delete while other operations are in flight
      if (savingCrew || savingMembers || deletingCrew) return;

      const crewId = selectedCrew.id;
      const crewName = selectedCrew.name || "this crew";

      await presentAlert({
        header: "Delete Crew?",
        message: `This will permanently delete "${crewName}". This action cannot be undone.`,
        buttons: [
          {
            text: "Cancel",
            role: "cancel",
          },
          {
            text: "Delete",
            role: "destructive",
            handler: async () => {
              try {
                setDeletingCrew(true);

                // Close child modals first to avoid “orphaned” UI
                setEditMembersOpen(false);
                setCreateCrewOpen(false);

                // Clear selected crew + close details modal for safety
                setSelectedCrew(null);
                setCrewModalOpen(false);

                await deleteCrew({ crewId });

                setDeletingCrew(false);
              } catch (err: any) {
                console.error("Delete crew error:", err);
                setDeletingCrew(false);

                await presentAlert({
                  header: "Couldn’t Delete Crew",
                  message: err?.message || "Unknown error",
                  buttons: ["OK"],
                });
              }
            },
          },
        ],
      });
    } catch (err) {
      // presentAlert itself can throw in some edge cases; just log.
      console.error("Delete crew alert error:", err);
    }
  };

  // Save Crew Members Handler
  const handleSaveMembers = async () => {
    try {
      if (!selectedCrew?.id) return;

      setSavingMembers(true);

      await updateCrewMembers({
        crewId: selectedCrew.id,
        memberIds: selectedMemberIds,
      });

      setSavingMembers(false);
      setEditMembersOpen(false);
    } catch (err: any) {
      console.error("Save members error:", err);
      setSavingMembers(false);

      await presentAlert({
        header: "Couldn’t Save Members",
        message: err?.message || "Unknown error",
        buttons: ["OK"],
      });
    }
  };

  // Crews View
  const openCrewModal = (crew: Crew) => {
    blurFocusedElement();
    setSelectedCrew(crew);
    setCrewModalOpen(true);
  };

  const closeCrewModal = () => {
    blurFocusedElement();
    setCrewModalOpen(false);
    setSelectedCrew(null);
  };

  const searchPlaceholder = useMemo(() => {
    return viewMode === "crews" ? "Search crews..." : undefined;
  }, [viewMode]);

  if (!authReady) {
    return <LoadingScreen LoadingText="Loading" />;
  }

  if (currentUser.uid === "") {
    return <Redirect to="login" />;
  }

  // Desktop mode - render desktop version
  if (isDesktop()) {
    return <AssociatesDesktop />;
  }

  return (
    <IonPage
    >
      <WerkerSchedulerHeader title={HEADERTEXT_TYPES.Associates} />

      {/* Conditionally render If in Sched Mode; Segment: Associates | Crews. */}
      {canSeeCrews && (
        <div style={{ paddingTop: 10, paddingBottom: 4 }}>
          <IonSegment
            value={viewMode}
            onIonChange={(e) => {
              const val = (e.detail.value as AssociatesViewMode) || "associates";
              sessionStorage.setItem("associates_view_mode", val);
              setViewMode(val);
            }}
          >
            <IonSegmentButton
              value="associates"
            >
              <IonLabel>
                Associates
              </IonLabel>
            </IonSegmentButton>

            <IonSegmentButton
              value="crews"
            >
              <IonLabel>
                Crews
              </IonLabel>
            </IonSegmentButton>
          </IonSegment>
        </div>
      )}

      {/* Search (page-level) */}
      <IonRow
      >
        <IonCol
        >
          <IonSearchbar
            value={searchText}
            onIonChange={(e) => searchTextFunction(e.detail.value || "")}
            animated
            placeholder={searchPlaceholder}
          />
        </IonCol>
      </IonRow>

      <IonContent
      >
        {/* ========================= */}
        {/* ASSOCIATES VIEW            */}
        {/* ========================= */}
        {viewMode === "associates" && (
          <>
            {/* INCOMING REQUESTS — collapsible banner */}
            {incomingRequestsBase.length > 0 && (
              <div
                style={{
                  // margin: "8px 12px",
                  borderRadius: "10px",
                  backgroundColor: unreadIncomingCount > 0
                    ? "rgba(var(--ion-color-danger-rgb), 0.07)"
                    : "rgba(var(--ion-color-medium-rgb), 0.06)",
                  border: unreadIncomingCount > 0
                    ? "1px solid rgba(var(--ion-color-danger-rgb), 0.18)"
                    : "1px solid rgba(var(--ion-color-medium-rgb), 0.12)",
                  overflow: "hidden",
                }}
              >
                <div
                  onClick={() => setIncomingExpanded((prev) => !prev)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "12px 14px",
                    cursor: "pointer",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <IonIcon
                      icon={incomingExpanded ? chevronDown : chevronForward}
                      style={{
                        fontSize: "1.1rem",
                        color: unreadIncomingCount > 0
                          ? "var(--ion-color-danger)"
                          : "var(--ion-color-medium)",
                      }}
                    />
                    <span
                      style={{
                        fontWeight: unreadIncomingCount > 0 ? 700 : 500,
                        fontSize: "0.95rem",
                        color: unreadIncomingCount > 0
                          ? "var(--ion-text-color)"
                          : "var(--ion-color-medium)",
                      }}
                    >
                      Incoming Requests
                    </span>
                  </div>
                  <IonBadge
                    color={unreadIncomingCount > 0 ? "danger" : "medium"}
                    style={{ fontSize: "0.75rem" }}
                  >
                    {unreadIncomingCount > 0 ? unreadIncomingCount : incomingRequestsBase.length}
                  </IonBadge>
                </div>
                {incomingExpanded && (
                  <div style={{ padding: "0 14px 10px 14px" }}>
                    <AssociatesList associatesList={incomingRequests} />
                  </div>
                )}
              </div>
            )}

            {/* ASSOCIATES — flat scrollable list */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                margin: "12px 16px 2px 16px",
              }}
            >
              <span style={{ fontWeight: 700, fontSize: "1rem" }}>
                Associates
              </span>
              {associatesSortedBase.length > 0 && (
                <span
                  style={{
                    fontSize: "0.85rem",
                    color: "var(--ion-color-medium)",
                  }}
                >
                  {associatesSortedBase.length}
                </span>
              )}
            </div>
            <div
              style={{
                height: "1px",
                backgroundColor: "var(--ion-color-light-shade)",
                margin: "0 16px 4px 16px",
              }}
            />

            {associatesSorted.length === 0 ? (
              <p
                style={{
                  textAlign: "center",
                  color: "var(--ion-color-medium)",
                  padding: "24px 16px",
                  fontSize: "0.95rem",
                }}
              >
                {searchLower
                  ? "No matching associates."
                  : "Go make some friends."}
              </p>
            ) : (
              <AssociatesList associatesList={associatesSorted} />
            )}

            {/* EVERYONE — visible only when searching */}
            {showEveryone && (
              <>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    margin: "16px 16px 2px 16px",
                  }}
                >
                  <span style={{ fontWeight: 700, fontSize: "1rem" }}>
                    Everyone
                  </span>
                  {everyoneElse?.length > 0 && (
                    <span
                      style={{
                        fontSize: "0.85rem",
                        color: "var(--ion-color-medium)",
                      }}
                    >
                      {everyoneElse.length}
                    </span>
                  )}
                </div>
                <div
                  style={{
                    height: "1px",
                    backgroundColor: "var(--ion-color-light-shade)",
                    margin: "0 16px 4px 16px",
                  }}
                />

                {everyoneFiltered.length === 0 ? (
                  <p
                    style={{
                      textAlign: "center",
                      color: "var(--ion-color-medium)",
                      padding: "24px 16px",
                      fontSize: "0.95rem",
                    }}
                  >
                    No matches.
                  </p>
                ) : (
                  <AssociatesList associatesList={everyoneFiltered} />
                )}
              </>
            )}
          </>
        )}

        {/* ===================== */}
        {/* CREWS VIEW (existing) */}
        {/* ===================== */}
        {viewMode === "crews" && canSeeCrews && (
          <CrewsListSection
            crewsLoading={crewsLoading}
            crewsFiltered={crewsFiltered}
            searchLower={searchLower}
            onCreateCrew={openCreateCrew}
            onOpenCrew={openCrewModal}
            getInitials={getInitials}
          />
        )}

        {/* Crew Details Modal */}
        <CrewDetailsModal
          isOpen={crewModalOpen}
          onDidDismiss={closeCrewModal}
          selectedCrew={selectedCrew}
          onEditCrewDetails={openEditCrewDetails}
          onOpenEditMembers={openEditMembers}
          onDeleteCrew={handleDeleteCrew}
          deletingCrew={deletingCrew}
          savingMembers={savingMembers}
          savingCrew={savingCrew}
          crewMembersResolved={crewMembersResolved}
          getInitials={getInitials}
        />

        {/* Edit Crew Members Modal (filter + select-all lives ONLY here) */}
        <EditCrewMembersModal
          isOpen={editMembersOpen}
          onDidDismiss={closeEditMembers}
          onSave={handleSaveMembers}
          savingMembers={savingMembers}
          memberSearchText={memberSearchText}
          setMemberSearchText={setMemberSearchText}
          memberSearchLower={memberSearchLower}
          toggleSelectAllVisible={toggleSelectAllVisible}
          allVisibleSelected={allVisibleSelected}
          someVisibleSelected={someVisibleSelected}
          visibleSelectedCount={visibleSelectedCount}
          visibleUidsCount={visibleUids.length}
          filteredSortedMembers={filteredSortedMembers}
          selectedMemberIds={selectedMemberIds}
          toggleMember={toggleMember}
        />

        {/* Create Crew Modal */}
        <CreateCrewModal
          isOpen={createCrewOpen}
          onDidDismiss={closeCreateCrew}
          editingCrewId={editingCrewId}
          newCrewName={newCrewName}
          setNewCrewName={setNewCrewName}
          newCrewDescription={newCrewDescription}
          setNewCrewDescription={setNewCrewDescription}
          onSave={handleSaveCrewDetails}
          savingCrew={savingCrew}
        />
      </IonContent>

      <TabNavigator />
    </IonPage>
  );
};

export default Associates;
